import { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { API_BASE_URL } from '../../api';

const MAX_RECENT_UPDATES = 30;

const describeUpdate = (update) => {
  const verb = { created: 'added', updated: 'updated', deleted: 'removed' }[update.action] || update.action;
  return `${update.adminName || 'Someone'} ${verb} "${update.name}"`;
};

/**
 * Connects to the admin realtime socket: WhatsApp-style presence (who's
 * online, from where, on what device, doing what) and a live feed of
 * catalog changes (product created/updated/deleted) from any admin.
 *
 * Presence entries are keyed by session (one per login/device), not by
 * admin — the same admin account signed in on both web and mobile shows up
 * as two distinct entries, which is what lets this surface "also signed in
 * on: ..." instead of just disappearing into "no one else online".
 */
export default function usePresence({ activeTab, currentAdminId, showNotification }) {
  const [sessions, setSessions] = useState([]);
  const [mySessionId, setMySessionId] = useState(null);
  const [recentUpdates, setRecentUpdates] = useState([]);
  const socketRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;

    const socket = io(API_BASE_URL || window.location.origin, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('presence:self', ({ sessionId }) => setMySessionId(sessionId));

    socket.on('presence:snapshot', (snapshot) => {
      setSessions(snapshot.filter(Boolean));
    });

    socket.on('presence:update', (presence) => {
      setSessions((prev) => {
        const withoutThisSession = prev.filter((s) => s.sessionId !== presence.sessionId);
        if (presence.status === 'offline') return withoutThisSession;
        return [...withoutThisSession, presence];
      });
    });

    socket.on('catalog:update', (update) => {
      setRecentUpdates((prev) => [update, ...prev].slice(0, MAX_RECENT_UPDATES));
      // Don't toast an admin's own action back at them — they already know.
      if (update.adminId !== currentAdminId && showNotification) {
        showNotification(describeUpdate(update), 'success');
      }
    });

    return () => socket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Let everyone else see which tab/screen each admin is currently on.
  useEffect(() => {
    socketRef.current?.emit('presence:activity', { page: activeTab });
  }, [activeTab]);

  const otherOnlineAdmins = sessions.filter((s) => s.adminId !== currentAdminId);
  const myOtherSessions = sessions.filter((s) => s.adminId === currentAdminId && s.sessionId !== mySessionId);

  return { onlineAdmins: otherOnlineAdmins, myOtherSessions, recentUpdates };
}
